// ─────────────────────────────────────────────────────────────────────────────
// The clock in the topbar — today's Gregorian date and the time of day.
//
// It began as its own strip stacked above the topbar. One bar, not two: a
// second full-width row of chrome costs every page twenty pixels of height
// forever, to carry two facts that fit in the slack the topbar already has.
//
// ── WHY IT SITS WITH THE BRAND, AT THE INLINE-START ─────────────────────────
//
// The topbar is `1fr auto 1fr`, and that grid is load-bearing: the middle
// track is what keeps the language toggle genuinely centred, which is the fix
// for the RTL mis-click described in Shell.tsx. So the clock had to join one
// of the two 1fr tracks rather than become a fourth column, which would have
// moved the centre off the centre.
//
// Of the two, the start track:
//
//   • THE CLOCK IS STATUS, NOT AN ACTION. The end track is a row of controls —
//     Notifications, Sign out. A read-only label dropped among buttons reads
//     as a disabled one.
//   • THAT TRACK IS ALSO THE ONE THAT OVERFLOWS. It holds two controls already
//     and is the place where a fifth item would push Sign out off the edge on
//     a phone. The start track holds one short label and has the slack.
//   • It mirrors correctly for free. `.topbar-lead` is a flex row in the
//     document's direction, so in Arabic the brand and the clock sit at the
//     right, still in that reading order.
//
// ── THE NARROW CASE ─────────────────────────────────────────────────────────
//
// Below 640px the DATE is hidden and the time stays, because the time is the
// part that is only available here — the date is on every screen that shows a
// date, and it is the one people already know. The brand ellipsises before
// either of them; the clock is `flex: none` so it never squeezes and never
// wraps the bar onto a second line.
//
// ── IT TICKS ────────────────────────────────────────────────────────────────
//
// A clock that was right at nine and is twenty minutes out by eleven is worse
// than no clock: a wrong number that looks authoritative gets believed, and
// this one sits beside a register people reconcile against. It re-reads the
// wall clock every second and renders only when the displayed minute changes.
//
// ── WHOSE TIME ──────────────────────────────────────────────────────────────
//
// The BRANCH's, from `workspace.navigation` by way of `useFormat`. Attendance,
// payroll and the calendar are all computed in the branch's zone. Somebody
// with no branch gets Asia/Riyadh — clinic time; see `timezoneFor` in the
// workspace service. The zone is named in the tooltip, where it is there for
// the one conversation that needs it without being a third thing on the bar.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState, type ReactElement } from "react";
import { useI18n } from "../i18n";
import { useFormat } from "../i18n/format";

/** Separator for the packed state string. Cannot occur in a date or a time. */
const SEP = "|";

export function TopbarClock(): ReactElement {
  const { t } = useI18n();
  const fmt = useFormat();

  // The STRING is the state, not the Date. A new `Date` every second would be
  // a new object every second and so a render every second; a string that has
  // not changed makes `setState` a no-op, so this renders when the displayed
  // minute rolls over and not otherwise.
  const [stamp, setStamp] = useState(() => pack(fmt));

  useEffect(() => {
    const tick = (): void => { setStamp(pack(fmt)); };
    // Once immediately: `fmt` changes when the branch's zone arrives from the
    // server, and until then the clock is showing the fallback zone.
    tick();
    const id = window.setInterval(tick, 1000);
    return () => { window.clearInterval(id); };
  }, [fmt]);

  const [date = "", clock = ""] = stamp.split(SEP);

  return (
    // `dir="ltr"` on the pair, not on the bar. `2026-08-10` and `3:40 م` are
    // Latin-digit runs either side of a separator, which is exactly the shape
    // the bidi algorithm reorders inside an RTL line — the time would jump in
    // front of the date. The Arabic marker stays with its own time because it
    // is inside the isolated run, not beside it.
    <span className="topbar-clock" dir="ltr" title={t("common.clockZoneTitle", { zone: fmt.timezone })}>
      <span className="topbar-clock-date">{date}</span>
      <span className="topbar-clock-sep" aria-hidden="true">·</span>
      {/* Minute granularity, so announcing it politely is once a minute. */}
      <span className="topbar-clock-time" aria-live="polite">{clock}</span>
    </span>
  );
}

function pack(fmt: ReturnType<typeof useFormat>): string {
  const now = new Date();
  return `${fmt.date(now)}${SEP}${fmt.clock(now)}`;
}
